import Form from "components/Reusable/Form/Form";
import Icon from "components/Elements/Icon/Icon";
import SuccessSubmit from "../SuccessSubmit";
import Tooltip from "components/Elements/Tooltip/Tooltip";
import useOnClickOutside from "hooks/useOnClickOutside";

import { ButtonAccessability } from "components/Elements/Button/Button";
import { EMAIL_VALIDATOR, OLD_PHONE_VALIDATOR } from "helpers/validators";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { useUser } from "contexts/userContext/userContext";

import styles from "./contactUsForm.module.scss";

interface ContactUsFormI extends React.HTMLAttributes<HTMLDivElement> {
  showForm: boolean;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * ContactUsForm component is a floating form that is displayed
 * when the user clicks on the floating button on the promo page.
 */
const ContactUsForm = (props: ContactUsFormI) => {
  const { showForm, setShowForm, ...restProps } = props;

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const { getLanguageCulture, getTextByKey, getDirection } = useContent();
  const { contactUs, getUserData } = useUser();

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showForm) {
      // Find the first input field and focus on it
      const input = wrapperRef.current?.querySelector("input");
      input?.focus();
    } else {
      setIsFormSubmitted(false);
    }
  }, [showForm]);

  useOnClickOutside(wrapperRef, () => {
    setShowForm(false);
  });

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
  }, [setShowForm]);

  const sendMessage = useCallback(
    async (data: any) => {
      try {
        const body: any = {
          ...data,
          ContactTitle: "Sales",
        };
        //Need for Email response API Call
        body.ContactCulture = getLanguageCulture().split("-")[0];
        body.Culture = getLanguageCulture().toLowerCase();
        body.MailType = "ContactUsMail";
        body.userIp = getUserData().User_IP;
        body.platform = getUserData().User_Platform;

        const response = await contactUs(body);

        if (response.status === "success") {
          setIsFormSubmitted(true);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [contactUs, getLanguageCulture, getUserData]
  );

  const fields = {
    ContactEmail: {
      fieldKey: "ContactEmail",
      label: "email",
      type: "email",
      validation: {
        required: true,
        invalidMessage: "invalid-email-text",
        reg: EMAIL_VALIDATOR,
      },
      placeholder: "",
    },
    ContactSender: {
      fieldKey: "ContactSender",
      label: "name",
      type: "text",
      placeholder: "",
      validation: {
        required: true,
        reg: /[^\n]+/,
        invalidMessage: "default-input-invalid-message",
      },
    },
    ContactPhone: {
      fieldKey: "ContactPhone",
      label: "phone",
      type: "tel",
      placeholder: "",
      validation: {
        required: true,
        reg: OLD_PHONE_VALIDATOR,
        invalidMessage: "default-input-invalid-message",
      },
    },
    ContactMessage: {
      fieldKey: "ContactMessage",
      label: "message",
      type: "text",
      element: "textarea",
      placeholder: "",
    },
  };

  return (
    <div
      className={`${styles.wrapper} ${showForm ? "" : styles.hidden} ${
        isFormSubmitted ? styles.successFormWrapper : ""
      } ${getDirection() === "rtl" ? styles.rtl : ""}`}
      {...restProps}
      ref={wrapperRef}
    >
      <ButtonAccessability
        onPointerDown={handleCloseForm}
        onClick={handleCloseForm}
        buttonClassName={styles.closeIcon}
        ariaLabel="Close Contact Us Form"
      >
        {/*@ts-ignore*/}
        <Tooltip id="close-contact-us-form" tooltipTextKey="close">
          <Icon name="close" small gray />
        </Tooltip>
      </ButtonAccessability>
      {!isFormSubmitted ? (
        <>
          <h2 className={styles.title}>{getTextByKey("contact-us")}</h2>
          <p className={styles.description}>
            {getTextByKey("contact-us-description")}
          </p>
        </>
      ) : null}
      {showForm && isFormSubmitted ? <SuccessSubmit /> : null}
      <Form
        /*@ts-ignore*/
        fields={fields}
        onSubmit={sendMessage}
        submitProps={{
          title: getTextByKey("submit"),
          className: isFormSubmitted ? "" : styles.blankButton,
        }}
        buttonProps={{ buttonsSize: "large" }}
        clearAfterSubmit
        // captchaProvider="cloudflare"
        // cloudflareActionName="contactUs"
      />
    </div>
  );
};

export default memo(ContactUsForm);
